"use client";
import React, { useState, useEffect } from "react";
import PropTypes from "prop-types";
import dynamic from "next/dynamic";

import { useSelector, useDispatch } from "react-redux";

import { handleLastRemovedGroupInfo, handleFavoriteInfoBarButtonContent } from "@/src/store/favorite";
import { triggerInfoBarOpened, handleStickyContent } from "@/src/store/stickyInfoBar";
import { handleListingId } from "@/src/store/listingSearch";

import { favoriteHelpers } from "@/src/helpers/favorite";

import FavoriteIcon from "assets/svg/favourite_icon.svg";
import FavoriteFillIcon from "assets/svg/favorite_fill_icon.svg";

import { infoRemoveToListing } from "constants/favorite";

import { removeFavoriteListing } from "services/favorites";

import { isTokenExpired, getNewToken, getToken } from "@/src/helpers/auth";
import { Ga4WithEventParameters } from "@/src/helpers/google-tag-manager/events/ga4-with-eventparameters";
import { normalizeTurkishText } from "@/src/helpers/normalize-turkish-text";

import ModalTextContent from "components/modal-text-content";
import ModalFavorite from "../../listing-detail/modal-favorite";
import LoginStepModalContent from "../login-step-modal-content";

const Modal = dynamic(() => import("components/common/modal"), { ssr: false });

const AddFavoriteButton = ({ listingId, cityName, districtName, townName, localityName, onTrackPurchase }) => {
  const useFavorite = (id) => {
    const [isFavorite, setIsFavorite] = useState(false);
    const favoriteGroups = useSelector((state) => state.favorite.favoriteList);
    useEffect(() => {
      const checkIsFavorite = () => {
        return favoriteGroups?.some((group) => group.idList.includes(id));
      };
      setIsFavorite(checkIsFavorite());
    }, [favoriteGroups]);
    return [isFavorite, setIsFavorite];
  };

  const dispatch = useDispatch();
  const getAllFavoritesByAccount = favoriteHelpers();

  const [isFavorite, setIsFavorite] = useFavorite(listingId);

  const [isModalOpen, setIsModalOpen] = useState(false);

  const isAuth = useSelector((state) => state.auth.isAuth);

  const removeFavorite = async () => {
    dispatch(handleFavoriteInfoBarButtonContent("Geri Al"));
    let response;
    try {
      response = await removeFavoriteListing(listingId);
      if (response.status === "success") {
        await getAllFavoritesByAccount();
        setIsFavorite(false);
        dispatch(handleStickyContent(infoRemoveToListing(response.result?.title)));
        dispatch(handleLastRemovedGroupInfo(response.result));
        dispatch(triggerInfoBarOpened());
      } else if (response.status === "error") {
        handleErrorStickyContent(response);
      }
    } catch (error) {
      console.error(`Error from removeFavorite: `, error);
      handleErrorStickyContent(response);
    }
  };

  const handleErrorStickyContent = (responseParam) => {
    const message = responseParam?.description?.tr || "Bir hata oluştu.";
    dispatch(handleStickyContent(message));
    dispatch(handleFavoriteInfoBarButtonContent(""));
    dispatch(triggerInfoBarOpened());
  };

  const setFavorite = () => {
    dispatch(
      handleFavoriteInfoBarButtonContent(
        <a
          href={`${
            window.env.EJ_APPS_PANEL_HOSTNAME
          }/#/member/authorize?token=${getToken()}&redirectUrl=favori-listelerim`}
          target="blank"
        >
          Listeye Git
        </a>
      )
    );
    setIsModalOpen(true);
  };

  const handleFavoriteButton = async (event) => {
    event.preventDefault();
    dispatch(handleListingId(listingId));
    Ga4WithEventParameters({ eventName: "click_favorite", eventParameters: { item_id: listingId.toString() } });
    if (!getToken()) {
      setIsModalOpen(true);
      return;
    }
    if (isTokenExpired()) await getNewToken();

    if (!isFavorite) {
      setFavorite();
    } else {
      removeFavorite();
    }
  };

  const handleLoginSuccessCallback = async () => {
    await getAllFavoritesByAccount((favoriteResponse) => {
      favoriteResponse?.map((item) => item.idList.includes(listingId) && setIsModalOpen(false));
    });
    if (isFavorite) setIsModalOpen(false);
  };

  const handleModalClose = () => {
    setIsModalOpen(false);
  };

  const handleModalFavoriteSuccesCallback = async () => {
    await getAllFavoritesByAccount();
    Ga4WithEventParameters({
      eventName: "favorite",
      eventParameters: {
        item_id: listingId.toString(),
        item_type: "ilan",
        city: normalizeTurkishText(cityName),
        town: normalizeTurkishText(districtName),
        neighborhood: normalizeTurkishText(townName),
        district: normalizeTurkishText(localityName) || undefined,
      },
    });

    try {
      onTrackPurchase?.();
    } catch (e) {
      console.error("onTrackPurchase error:", e);
    }

    setIsFavorite(true);
    setIsModalOpen(false);
  };

  const handleModalFavoriteFailureCallback = async () => {
    setIsModalOpen(false);
  };

  useEffect(() => {
    if (!isAuth) setIsFavorite(false);
  }, [isAuth]);

  return (
    <>
      <span onClick={(e) => handleFavoriteButton(e)}>{!isFavorite ? <FavoriteIcon /> : <FavoriteFillIcon />}</span>
      {isModalOpen && (
        <Modal isOpen={isModalOpen} handleClose={() => handleModalClose()}>
          <ModalTextContent>
            {!getToken() ? (
              <LoginStepModalContent handleLoginSuccessCallback={handleLoginSuccessCallback} />
            ) : (
              <ModalFavorite
                listingId={listingId}
                successCallback={handleModalFavoriteSuccesCallback}
                failureCallback={handleModalFavoriteFailureCallback}
              />
            )}
          </ModalTextContent>
        </Modal>
      )}
    </>
  );
};

AddFavoriteButton.propTypes = {
  listingId: PropTypes.number,
  cityName: PropTypes.string,
  districtName: PropTypes.string,
  townName: PropTypes.string,
  localityName: PropTypes.string,
  onTrackPurchase: PropTypes.func,
};

export default AddFavoriteButton;
